import React, { useEffect, useState } from "react";
import { X, FileSignature, ShieldCheck, Calendar, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Tap In TV Network — Creator Waiver & Agreement modal.
 *
 * Renders the full 11-section legal document pulled from /api/waiver/status,
 * plus mandatory acknowledgment checkboxes (original content, no copyright,
 * responsibility, quarterly renewal) and a profile snapshot (printed name,
 * stage name, phone, address). All of this is posted to /api/waiver/accept
 * and persisted in the users collection + waiver_signatures ledger for audit.
 *
 * Renewal: every 90 days. The modal will auto-open from any gated action
 * when /waiver/status returns accepted:false.
 */
export default function WaiverModal({ open, onClose, onAccepted }) {
  const [waiver, setWaiver] = useState(null);
  const [signature, setSignature] = useState("");
  const [printedName, setPrintedName] = useState("");
  const [stageName, setStageName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [acks, setAcks] = useState({
    original_content: false,
    no_copyright_violation: false,
    assumes_responsibility: false,
    quarterly_renewal: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/waiver/status").then((r) => setWaiver(r.data)).catch(() => {});
  }, [open]);

  if (!open) return null;

  const allAcked = Object.values(acks).every(Boolean);
  const canSubmit = allAcked && signature.trim().length >= 3 && printedName.trim().length >= 3;

  const submit = async () => {
    if (!canSubmit) { toast.error("Please fill in your name, sign, and tick every acknowledgment."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/waiver/accept", {
        signature: signature.trim(),
        printed_name: printedName.trim(),
        stage_name: stageName.trim(),
        phone: phone.trim(),
        address: address.trim(),
        acknowledgments: acks,
      });
      const renews = data.expires_at ? new Date(data.expires_at).toLocaleDateString() : "in 90 days";
      toast.success(`Waiver signed — valid until ${renews}`);
      onAccepted?.(data);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Unable to save waiver";
      toast.error(typeof msg === "string" ? msg : "Unable to save waiver");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="waiver-modal" className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="tv-glass rounded-2xl w-full max-w-3xl border border-white/10 overflow-hidden flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between p-5 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-3">
            <FileSignature className="w-5 h-5 text-tv-accent" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-tv-accent font-bold">📄 {waiver?.subtitle || "Quarterly Renewal Required"}</div>
              <div className="font-display font-black text-white text-lg sm:text-xl tracking-tight leading-tight">{waiver?.title || "Creator Waiver & Agreement"}</div>
            </div>
          </div>
          <button data-testid="waiver-close-btn" onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          {!waiver ? (
            <div className="flex items-center gap-2 text-white/60 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading agreement…</div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className="bg-white/10 text-white/70 px-2 py-1 rounded">Version {waiver.current_version}</span>
                <span className="bg-tv-accent/10 text-tv-accent px-2 py-1 rounded flex items-center gap-1"><Calendar className="w-3 h-3" /> Valid for {waiver.duration_days} days after signing</span>
                {waiver.accepted && waiver.expires_at && (
                  <span className="bg-emerald-500/10 text-emerald-300 px-2 py-1 rounded">Current waiver expires {new Date(waiver.expires_at).toLocaleDateString()}</span>
                )}
              </div>

              {/* Sections */}
              <div className="space-y-4">
                {waiver.sections?.map((s) => (
                  <section key={s.n} data-testid={`waiver-section-${s.n}`} className="border-l-2 border-tv-accent/30 pl-4">
                    <h3 className="font-display font-bold text-white text-sm tracking-tight">{s.n}. {s.heading}</h3>
                    {s.body && <p className="text-white/75 text-sm mt-1.5 leading-relaxed">{s.body}</p>}
                    {s.bullets && (
                      <ul className="mt-1.5 text-white/75 text-sm list-disc pl-5 space-y-1">
                        {s.bullets.map((b, i) => <li key={i}>{b}</li>)}
                      </ul>
                    )}
                  </section>
                ))}
              </div>

              {/* Required acknowledgments */}
              <div className="mt-6 bg-white/5 border border-white/10 rounded-lg p-4">
                <div className="text-[10px] uppercase tracking-wider text-tv-accent font-bold mb-3 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" /> Required acknowledgments
                </div>
                {[
                  ["original_content", "All content I upload is original or I have proper rights/licenses."],
                  ["no_copyright_violation", "I will not violate copyright, trademark, or any third-party IP."],
                  ["assumes_responsibility", "I assume full legal responsibility for content I upload or stream, and release Tap In TV Network from liability."],
                  ["quarterly_renewal", "I understand this agreement is valid for 90 days and must be renewed to keep upload, live, and monetization access."],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-start gap-3 py-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`waiver-ack-${key}`}
                      checked={acks[key]}
                      onChange={(e) => setAcks({ ...acks, [key]: e.target.checked })}
                      className="mt-0.5 accent-tv-accent"
                    />
                    <span className="text-white/85 text-sm">{label}</span>
                  </label>
                ))}
              </div>

              {/* Profile snapshot */}
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                <Field label="Printed name (full legal) *" testid="waiver-printed-name" value={printedName} onChange={setPrintedName} />
                <Field label="Stage name (optional)" testid="waiver-stage-name" value={stageName} onChange={setStageName} />
                <Field label="Phone" testid="waiver-phone" value={phone} onChange={setPhone} placeholder="+1 555 000 0000" />
                <Field label="Address" testid="waiver-address" value={address} onChange={setAddress} placeholder="Street, City, State/ZIP" />
              </div>

              {/* Signature */}
              <div className="mt-4">
                <label className="text-[10px] uppercase tracking-wider text-white/60 font-bold">✍️ Type your signature (legal name)</label>
                <input
                  data-testid="waiver-signature-input"
                  type="text"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder="Nova Reyes"
                  className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-3 text-white text-lg font-display italic outline-none focus:border-tv-accent"
                />
                <div className="text-[11px] text-white/50 mt-1">By typing your name and clicking Sign, you agree this constitutes your legal signature.</div>
              </div>
            </>
          )}
        </div>

        <div className="p-5 border-t border-white/10 flex items-center justify-end gap-3 shrink-0">
          <button data-testid="waiver-cancel-btn" onClick={onClose} className="text-white/70 hover:text-white font-semibold px-4 py-2 rounded-full">Cancel</button>
          <button
            data-testid="waiver-accept-btn"
            onClick={submit}
            disabled={!canSubmit || busy}
            className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-6 py-2.5 rounded-full disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />}
            {busy ? "Saving…" : "Sign & Agree"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">{label}</span>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent"
      />
    </label>
  );
}
